import { useEffect, useState, ReactNode } from "react";

interface AuthShellProps {
    children: ReactNode;
    heading: string;
    subheading: string;
}

export const AuthShell = ({ children, heading, subheading }: AuthShellProps) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 sm:p-8 relative">
            {/* ── Background decoration ── */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary-900/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" />
                <div
                    className="absolute inset-0 opacity-[0.015]"
                    style={{
                        backgroundImage: "linear-gradient(0deg,#000 1px,transparent 1px),linear-gradient(90deg,#000 1px,transparent 1px)",
                        backgroundSize: "32px 32px",
                    }}
                />
            </div>

            <div
                className={`relative w-full max-w-[420px] transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                    }`}
            >
                {/* ── Brand Logo Header ── */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-sm border border-slate-100 mb-5 relative">
                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-50 opacity-50 rounded-b-2xl pointer-events-none" />
                        <svg className="w-10 h-10 text-primary-900" viewBox="0 0 100 100" fill="currentColor">
                            <path d="M49.5 24.3c-13.8 0-25 11.2-25 25s11.2 25 25 25c12.2 0 22.3-8.8 24.5-20.4L58.6 50.1c-1.3 4.2-5.1 7.2-9.6 7.2-5.5 0-10-4.5-10-10s4.5-10 10-10c3 0 5.6 1.3 7.5 3.3l11.4-11.4C62.8 26.6 56.6 24.3 49.5 24.3z" />
                            <path d="M78 35.8L63.5 21.3v9H49.5v11h14v9z" />
                        </svg>
                    </div>
                    <h1 className="font-display font-extrabold text-slate-900 text-2xl md:text-3xl tracking-tight">
                        BudgIT <span className="text-primary-600">Admin</span>
                    </h1>
                    <p className="font-body text-slate-500 font-medium mt-2">
                        Secure Academy Management Console
                    </p>
                </div>

                {/* ── Content Card ── */}
                <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-slate-900/5 px-6 py-8 md:px-8 md:py-10">
                    <div className="mb-8">
                        <h2 className="font-display text-2xl font-bold text-slate-900">{heading}</h2>
                        <p className="font-body text-slate-500 text-sm mt-1.5 flex items-center gap-1.5">
                            <span>{subheading}</span>
                        </p>
                    </div>

                    {children}
                </div>

                {/* ── Footer ── */}
                <div className="text-center mt-8">
                    <div className="inline-flex items-center justify-center gap-2 text-xs font-medium text-slate-400 bg-white/50 backdrop-blur px-4 py-2 rounded-full ring-1 ring-slate-200">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        End-to-end encrypted session
                    </div>
                    <p className="font-body text-xs text-slate-400 mt-6">
                        © {new Date().getFullYear()} BudgIT Foundation. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};
